/**
 * useEvidenceTracks
 * React Query mutation hooks for evidence track actions:
 * - useUpdateDocumentsTrack — PATCH documents track status
 * - useUpdateCodeScanTrack — PATCH code-scan track status
 * - useUpdateInfraTrack — PATCH infrastructure track status
 * - useStartAnalysis — POST start-analysis, navigate on success
 *
 * All three PATCH mutations consume the server-returned
 * { trackStatuses: EvidenceTrackStatuses } response and patch only the
 * `trackStatuses` slice of the cached assessment via `setQueryData` —
 * avoiding the full refetch that an `invalidateQueries` call would trigger.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import apiClient from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { EVIDENCE_PROCESSING_ERROR_CODE } from "@app/types";
import type { DocumentsTrackStatus, EvidenceTrackStatuses } from "@app/types";

type TrackEndpointSegment = "documents" | "code-scan" | "infrastructure";

interface TrackPatchResponse {
  trackStatuses: EvidenceTrackStatuses;
}

/**
 * Shared mutation config factory for the three evidence-track PATCH hooks.
 *
 * Centralises the cache-patch logic so callers get consistent behaviour:
 * - Throws "No tenant selected" if tenant context is missing.
 * - Surfaces API errors through the toast system.
 * - On success, patches only the `trackStatuses` field of the cached
 *   assessment (does NOT invalidate the full assessment query).
 *
 * Keeping this as an internal hook (starts with `use`) means each concrete
 * hook just threads type arguments and endpoint-specific metadata through.
 */
function useTrackStatusMutation<TStatus extends string>(params: {
  assessmentId: string;
  endpointSegment: TrackEndpointSegment;
  errorDefault: string;
  onRefetch?: () => void;
}) {
  const { assessmentId, endpointSegment, errorDefault, onRefetch } = params;
  const { currentTenant } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (status: TStatus) => {
      if (!currentTenant) throw new Error("No tenant selected");

      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/assessments/${assessmentId}/tracks/${endpointSegment}` as any;
      const { data, error } = await apiClient.PATCH(url, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { status } as any,
      });

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error((error as any)?.message || errorDefault);
      }

      return data as TrackPatchResponse | undefined;
    },
    onSuccess: (data) => {
      // Patch only the trackStatuses slice of the cached assessment so the
      // UI picks up the new statuses without a full assessment refetch.
      // Falls back to no-op if the response shape is unexpected.
      if (currentTenant && data?.trackStatuses) {
        const queryKey = [
          "assessment",
          currentTenant.id,
          assessmentId,
        ] as const;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        queryClient.setQueryData<any>(queryKey, (cached: any) =>
          cached ? { ...cached, trackStatuses: data.trackStatuses } : cached,
        );
      }
      onRefetch?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || errorDefault,
        variant: "destructive",
      });
    },
  });
}

/**
 * Update the documents track status (OPEN | COMPLETE | SKIPPED).
 *
 * @param assessmentId - The assessment to update
 * @param onRefetch - Optional callback invoked after successful mutation (kept for backward compat)
 * @returns Mutation helpers for updating the documents track status
 */
export function useUpdateDocumentsTrack(
  assessmentId: string,
  onRefetch?: () => void,
) {
  const mutation = useTrackStatusMutation<DocumentsTrackStatus>({
    assessmentId,
    endpointSegment: "documents",
    errorDefault: "Failed to update documents track",
    onRefetch,
  });

  return {
    updateStatus: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}

/**
 * Update the code-scan track status (SKIPPED | ACTIVE).
 *
 * @param assessmentId - The assessment to update
 * @param onRefetch - Optional callback invoked after successful mutation (kept for backward compat)
 * @returns Mutation helpers for updating the code-scan track status
 */
export function useUpdateCodeScanTrack(
  assessmentId: string,
  onRefetch?: () => void,
) {
  const mutation = useTrackStatusMutation<"SKIPPED" | "ACTIVE">({
    assessmentId,
    endpointSegment: "code-scan",
    errorDefault: "Failed to update code scan track",
    onRefetch,
  });

  return {
    updateStatus: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}

/**
 * Update the infrastructure track status (SKIPPED | ACTIVE).
 *
 * @param assessmentId - The assessment to update
 * @param onRefetch - Optional callback invoked after successful mutation (kept for backward compat)
 * @returns Mutation helpers for updating the infra track status
 */
export function useUpdateInfraTrack(
  assessmentId: string,
  onRefetch?: () => void,
) {
  const mutation = useTrackStatusMutation<"SKIPPED" | "ACTIVE">({
    assessmentId,
    endpointSegment: "infrastructure",
    errorDefault: "Failed to update infrastructure track",
    onRefetch,
  });

  return {
    updateStatus: mutation.mutate,
    isUpdating: mutation.isPending,
  };
}

/** A failed start-analysis, carrying the API's error code when it sent one. */
type StartAnalysisError = Error & { code?: string };

/**
 * Build the error a failed start-analysis rejects with, keeping the server's
 * message and its `error` code (e.g. `EVIDENCE_PROCESSING`) so `onError` can
 * tell a wait-and-retry apart from a real failure.
 *
 * @param error - The API error body.
 * @returns The error for the mutation to reject with.
 */
function startAnalysisError(error: unknown): StartAnalysisError {
  const body = error as { error?: unknown; message?: unknown } | undefined;
  const message =
    typeof body?.message === "string" && body.message
      ? body.message
      : "Failed to start analysis";
  return Object.assign(new Error(message), {
    code: typeof body?.error === "string" ? body.error : undefined,
  });
}

/**
 * Start analysis — POST start-analysis endpoint, then navigate to questions page.
 *
 * @param assessmentId - The assessment to start analysis for
 * @param onRefetch - Callback invoked after successful mutation or on error
 * @returns Mutation helpers for starting analysis
 */
export function useStartAnalysis(assessmentId: string, onRefetch: () => void) {
  const { currentTenant } = useTenant();
  const { toast } = useToast();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!currentTenant) throw new Error("No tenant selected");

      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/assessments/${assessmentId}/start-analysis` as any;
      const { data, error } = await apiClient.POST(url, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: {} as any,
      });

      if (error) {
        throw startAnalysisError(error);
      }

      // Verify analysis was actually triggered — don't navigate if it wasn't
      const result = data as
        | { analysisTriggered?: boolean; message?: string }
        | undefined;
      if (result && !result.analysisTriggered) {
        throw new Error(result.message || "Analysis could not be started");
      }

      return data;
    },
    onSuccess: () => {
      onRefetch();
      navigate(`/assessments/${assessmentId}/questions`);
    },
    onError: (error: StartAnalysisError) => {
      onRefetch(); // Refresh state in case it was rolled back (or to pick up processing counts)
      if (error.code === EVIDENCE_PROCESSING_ERROR_CODE) {
        // Wait-and-retry, not a failure: uploads are still processing (sv-zlxt.4).
        toast({ title: "Still processing", description: error.message });
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to start analysis",
        variant: "destructive",
      });
    },
  });

  return {
    startAnalysis: mutation.mutate,
    isStarting: mutation.isPending,
  };
}
